// Operator-console i18n. A tiny, dependency-free provider over the DE/EN dictionary.
//
// - The OPERATOR interface is translated; customer-facing content (drafts, evidence, fact values)
//   is never translated.
// - The choice persists in localStorage ("neo_lang", the same key the legacy console used, so an
//   operator's language carries over) and is mirrored onto <html lang>.
import React from "react";
import {
  PRODUCT_NAME,
  PRODUCT_NAME_COMPOUND,
  PRODUCT_NAME_FULL,
} from "../../../product-name";
import { DICT, type Lang } from "./strings";

export type { Lang } from "./strings";

const STORAGE_KEY = "neo_lang";

type TVars = Record<string, string | number>;
export type TFunc = (key: string, vars?: TVars, fallback?: string) => string;

interface I18nValue {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: TFunc;
}

const I18nContext = React.createContext<I18nValue | null>(null);

function readInitialLang(): Lang {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "en" || v === "de") return v;
  } catch {
    /* localStorage may be unavailable */
  }
  return "de";
}

function interpolate(s: string, vars?: TVars): string {
  if (!vars) return s;
  return s.replace(/\{(\w+)\}/g, (m, k) => (k in vars ? String(vars[k]) : m));
}

/**
 * What the product calls itself (#782). Re-exported from `web/product-name.ts`, which the
 * landing reads too — one value for both surfaces, so the name is never spelled twice.
 */
export { PRODUCT_NAME, PRODUCT_NAME_FULL };

/**
 * A dictionary entry as it actually renders: the product's name resolved, plus any caller vars
 * (#782). Both tokens fill themselves in, so no call site passes a name.
 *
 * `{product}` resolves the full unspaced name. `{productCompound}` preserves the same name
 * inside a German compound, such as *ReqventOS-Postfach*. The dictionary retains the token
 * describing its sentence, even though both forms currently resolve to the same value.
 *
 * A caller's own variable still wins, which keeps these defaults rather than rules.
 *
 * Exported because the provider is not the only reader: a test asserting that the DOM shows a
 * given German string has to compare against the resolved form, not the raw dictionary value.
 */
export function resolveCopy(key: string, lang: Lang = "de", vars?: TVars, fallback?: string): string {
  const withProduct: TVars = { ...vars };
  if (withProduct.product === undefined) withProduct.product = PRODUCT_NAME_FULL;
  if (withProduct.productCompound === undefined) {
    withProduct.productCompound = PRODUCT_NAME_COMPOUND;
  }
  const entry = DICT[key];
  if (!entry) return interpolate(fallback ?? key, withProduct);
  return interpolate(entry[lang] ?? entry.de, withProduct);
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = React.useState<Lang>(readInitialLang);

  React.useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = React.useCallback((l: Lang) => {
    setLangState(l);
    try {
      localStorage.setItem(STORAGE_KEY, l);
    } catch {
      /* ignore */
    }
  }, []);

  const t = React.useCallback<TFunc>(
    (key, vars, fallback) => resolveCopy(key, lang, vars, fallback),
    [lang],
  );

  const value = React.useMemo<I18nValue>(() => ({ lang, setLang, t }), [lang, setLang, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

function useI18n(): I18nValue {
  const ctx = React.useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within <I18nProvider>");
  return ctx;
}

/** The translate function bound to the current language. */
export function useT(): TFunc {
  return useI18n().t;
}

/** Current language + setter (for the header switcher). */
export function useLang(): { lang: Lang; setLang: (l: Lang) => void } {
  const { lang, setLang } = useI18n();
  return { lang, setLang };
}

const LANGS: Array<{ code: Lang; label: string }> = [
  { code: "de", label: "DE" },
  { code: "en", label: "EN" },
];

/**
 * DE/EN segmented switch. Default is cream-on-navy for the app-shell header; `light` swaps to a
 * light-surface variant for use on a light background (e.g. the login form pane).
 */
export function LanguageSwitcher({ light }: { light?: boolean } = {}) {
  const { lang, setLang } = useLang();
  const { t } = useI18n();
  return (
    <div className={light ? "lang-switch lang-switch--light" : "lang-switch"} role="group" aria-label={t("lang.label")}>
      {LANGS.map(({ code, label }) => (
        <button
          key={code}
          type="button"
          className={code === lang ? "on" : ""}
          aria-pressed={code === lang}
          onClick={() => setLang(code)}
        >
          {label}
        </button>
      ))}
    </div>
  );
}
